import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { publicGuard } from './core/guards/public.guard';
import { roleGuard } from './core/guards/role.guard';
import { staffGuard } from './core/guards/staff.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { BACKOFFICE_ROLES } from './core/constants/roles';

/**
 * SmartClock BackOffice routes.
 *
 * Layout: auth (public routes) → login
 * Layout: main (protected routes) → home (gestión de cuentas, por construir)
 *
 * Mirror of SMARTCLOCK_WEBAPP src/app/app.routes.ts, reduced to the
 * BackOffice scope (providerAdmin).
 *
 * Cada sección exige además su clave de staff de «Ver» (staffGuard lee
 * `data.staffPermission`; falla cerrado si GET /access/me no responde):
 * Empresas → companies.read; Dispositivos → devices.manage; Comercial (Resumen, Bandeja, Leads, Campañas,
 * Contrataciones, Seguimiento mensual) → sales.read; Integraciones y Configuración comercial →
 * salesConfig.read; Facturación → billing.read;
 * Permisos de la web → access.read; Equipo → staff.read; Sesiones de
 * soporte → support.read; Accesos de fiscalizadores DT → inspection.read;
 * Mantenciones → maintenance.read; Correos enviados → emails.read.
 * Inicio y Mi cuenta quedan abiertos a todo staff.
 */
export const routes: Routes = [
  {
    path: '',
    redirectTo: '/app/home',
    pathMatch: 'full',
  },
  // Public routes (login)
  {
    path: '',
    loadComponent: () => import('./layouts/auth-layout/auth-layout').then((m) => m.AuthLayout),
    canActivate: [publicGuard],
    children: [
      {
        path: 'login',
        loadComponent: () => import('./views/auth/login/login.component').then((m) => m.Login),
      },
    ],
  },
  // Protected application shell
  {
    path: 'app',
    loadComponent: () => import('./layouts/main-layout/main-layout').then((m) => m.MainLayout),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: 'home',
        data: { roles: BACKOFFICE_ROLES },
        canActivate: [roleGuard],
        loadComponent: () => import('./views/home/home.component').then((m) => m.Home),
      },
      // Cuentas: empresas contratantes + ficha detallada
      {
        path: 'companies',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'companies.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/companies/companies.component').then((m) => m.Companies),
      },
      {
        path: 'companies/:id',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'companies.read' },
        canActivate: [roleGuard, staffGuard],
        // «Control de marcación» pregunta antes de descartar cambios sin guardar
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./views/companies/company-detail/company-detail.component').then(
            (m) => m.CompanyDetail,
          ),
      },
      {
        path: 'companies/:id/work-schedules',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'companies.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/companies/work-schedules/work-schedules.component').then(
            (m) => m.WorkSchedules,
          ),
      },
      // Dispositivos: monitoreo del parque de marcación de todas las empresas
      // (GET /devices/monitoring, staff devices.manage). El alta y la edición
      // viven en la ficha de cada empresa (pestaña «Dispositivos»).
      {
        path: 'devices',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'devices.manage' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/devices/devices-monitoring.component').then((m) => m.DevicesMonitoring),
      },
      // Fiscalización › Accesos de fiscalizadores DT (Art. 22.5): solicitudes de
      // clave, ingresos y empresas consultadas de TODAS las empresas
      // (GET /inspection/access-logs, staff inspection.read). PDF y Excel los genera el API.
      {
        path: 'inspection/access-logs',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'inspection.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/inspection/access-logs/access-logs.component').then(
            (m) => m.InspectionAccessLogs,
          ),
      },
      // Comercial › Contrataciones: las comerciales (link de pago desde el
      // BackOffice) y las del alta web. Ver: sales.read; los botones de
      // escritura exigen sales.manage (y «Reintentar alta», billing.manage).
      // Las rutas literales van antes de `signups/:id`.
      {
        path: 'signups',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'sales.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () => import('./views/signups/signups.component').then((m) => m.Signups),
      },
      {
        path: 'signups/new',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'sales.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/signups/signup-form/signup-form.component').then((m) => m.SignupForm),
      },
      {
        path: 'signups/:id',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'sales.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/signups/signup-detail/signup-detail.component').then(
            (m) => m.SignupDetail,
          ),
      },
      {
        path: 'signups/:id/edit',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'sales.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/signups/signup-form/signup-form.component').then((m) => m.SignupForm),
      },
      // Comercial › Seguimiento mensual (revenue.spec.md): cobrado, MRR y nuevas contrataciones
      // del mes con su detalle. Solo lectura con sales.read.
      {
        path: 'revenue',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'sales.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () => import('./views/revenue/revenue.component').then((m) => m.Revenue),
      },
      // Comercial › CRM (crm.spec.md §10): resumen, bandeja, leads, campañas
      // (sales.read) e integraciones y configuración comercial (salesConfig.read).
      // Las acciones de escritura exigen sales.manage / salesConfig.manage (el API valida).
      { path: 'crm', redirectTo: 'crm/summary', pathMatch: 'full' },
      {
        path: 'crm/summary',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'sales.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/crm/summary/crm-summary.component').then((m) => m.CrmSummaryView),
      },
      {
        path: 'crm/inbox',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'sales.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/crm/inbox/crm-inbox.component').then((m) => m.CrmInboxView),
      },
      {
        path: 'crm/leads',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'sales.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/crm/leads/crm-leads.component').then((m) => m.CrmLeadsView),
      },
      {
        path: 'crm/leads/:id',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'sales.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/crm/lead-detail/crm-lead-detail.component').then(
            (m) => m.CrmLeadDetailView,
          ),
      },
      {
        path: 'crm/campaigns',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'sales.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/crm/campaigns/crm-campaigns.component').then((m) => m.CrmCampaignsView),
      },
      {
        path: 'crm/integrations',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'salesConfig.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/crm/integrations/crm-integrations.component').then(
            (m) => m.CrmIntegrationsView,
          ),
      },
      {
        path: 'crm/integrations/:provider',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'salesConfig.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/crm/integrations/crm-integration-detail.component').then(
            (m) => m.CrmIntegrationDetailView,
          ),
      },
      {
        path: 'crm/settings',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'salesConfig.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/crm/settings/crm-settings.component').then((m) => m.CrmSettingsView),
      },
      // El antiguo marcador «Leads» (fase 2) vive ahora en el CRM
      { path: 'leads', redirectTo: 'crm/leads', pathMatch: 'full' },
      // ADM-09: historial de asignación de perfiles, transversal a las empresas
      {
        path: 'role-log',
        data: { roles: BACKOFFICE_ROLES },
        canActivate: [roleGuard],
        loadComponent: () => import('./views/role-log/role-log.component').then((m) => m.RoleLog),
      },
      // Auditoría › Correos enviados: todo correo del sistema, manual o automático, con lo que se
      // envió (email-log.spec.md). Solo lectura con emails.read.
      {
        path: 'audit/emails',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'emails.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/email-logs/email-logs.component').then((m) => m.EmailLogs),
      },
      // Matriz de perfiles y permisos administrativos
      {
        path: 'profile-permissions',
        data: { roles: BACKOFFICE_ROLES },
        canActivate: [roleGuard],
        loadComponent: () =>
          import('./views/profile-permissions/profile-permissions.component').then(
            (m) => m.ProfilePermissions,
          ),
      },
      // Plataforma › Mantenciones (Art. 17 e): programación con dos días hábiles de
      // anticipación, aviso en la plataforma y por correo, y seguimiento. Ver:
      // maintenance.read; programar/publicar/cancelar/finalizar: maintenance.manage (el API decide).
      {
        path: 'maintenance',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'maintenance.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/maintenance/maintenance.component').then((m) => m.MaintenanceView),
      },
      // Facturación SaaS (per-seat graduado + Flow)
      // Cobros del período: lista del mes, editor «Generar cobro» y detalle del cobro emitido
      {
        path: 'billing/charges',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'billing.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/billing/charges/charges.component').then((m) => m.Charges),
      },
      {
        path: 'billing/charges/new',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'billing.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/billing/charges/charge-editor/charge-editor.component').then(
            (m) => m.ChargeEditor,
          ),
      },
      {
        path: 'billing/charges/:invoiceId',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'billing.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/billing/charges/charge-detail/charge-detail.component').then(
            (m) => m.ChargeDetail,
          ),
      },
      {
        path: 'billing/pricing-schemes',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'billing.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/billing/pricing-schemes/pricing-schemes.component').then(
            (m) => m.PricingSchemes,
          ),
      },
      {
        path: 'billing/subscriptions',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'billing.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/billing/subscriptions/subscriptions.component').then(
            (m) => m.Subscriptions,
          ),
      },
      {
        path: 'billing/invoices',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'billing.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/billing/invoices/invoices.component').then((m) => m.Invoices),
      },
      // Permisos de la web (eje staff: access.read para ver, access.manage para escribir).
      // Los de cada empresa se configuran en su ficha (Roles y permisos, Módulos, Usuarios);
      // aquí quedan el catálogo y las plantillas base.
      {
        path: 'access/catalog',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'access.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/access/catalog/catalog.component').then((m) => m.AccessCatalogView),
      },
      {
        path: 'access/defaults',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'access.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/access/defaults/defaults.component').then((m) => m.AccessDefaults),
      },
      {
        path: 'access/closed',
        data: { roles: BACKOFFICE_ROLES },
        canActivate: [roleGuard],
        loadComponent: () =>
          import('./views/access/access-closed/access-closed.component').then(
            (m) => m.AccessClosed,
          ),
      },
      // Equipo y soporte: permisos del staff, «Ver como» y la cuenta propia (MFA)
      {
        path: 'staff',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'staff.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () => import('./views/staff/staff.component').then((m) => m.Staff),
      },
      {
        path: 'support-sessions',
        data: { roles: BACKOFFICE_ROLES, staffPermission: 'support.read' },
        canActivate: [roleGuard, staffGuard],
        loadComponent: () =>
          import('./views/support-sessions/support-sessions.component').then(
            (m) => m.SupportSessions,
          ),
      },
      {
        path: 'account',
        data: { roles: BACKOFFICE_ROLES },
        canActivate: [roleGuard],
        loadComponent: () => import('./views/account/account.component').then((m) => m.Account),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '/app/home',
  },
];
